/**
 * Purpose: Root application component — sets up routing, theme, and game state providers.
 * Owns: Route definitions, provider hierarchy, top-level layout.
 * Notes: Router type is selected at startup based on platform:
 *        - Web browser → BrowserRouter (clean URLs via HTML5 pushState)
 *        - Capacitor native (iOS/Android) → HashRouter (works with file:// and capacitor://)
 *        Detection uses Capacitor.isNativePlatform() at runtime. Override with
 *        VITE_FORCE_HASH_ROUTER=true in .env for local testing of hash routing.
 *        Mode-specific setup routes live at /setup/:slug (race, honor-roll, classroom).
 *        The old generic /setup route is kept for backwards compatibility.
 */

import { BrowserRouter, HashRouter, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from './theme/ThemeProvider';
import { GameProvider } from './context/GameContext';
import { HomePage } from './pages/HomePage';
import { SetupPage } from './pages/SetupPage';
import { RaceSetupPage } from './pages/setup/RaceSetupPage';
import { HonorRollSetupPage } from './pages/setup/HonorRollSetupPage';
import { ClassroomSetupPage } from './pages/setup/ClassroomSetupPage';
import { GameplayPage } from './pages/GameplayPage';
import { BoardPage } from './pages/BoardPage';
import { ResultsPage } from './pages/ResultsPage';
import { isNativePlatform } from './utils/platform';

/**
 * All application routes — shared between BrowserRouter and HashRouter.
 * Extracted so route definitions stay in one place regardless of router type.
 */
function AppRoutes() {
  return (
    <ThemeProvider>
      <GameProvider>
        <Routes>
          <Route path="/" element={<HomePage />} />
          {/* Mode-specific setup routes */}
          <Route path="/setup/race" element={<RaceSetupPage />} />
          <Route path="/setup/honor-roll" element={<HonorRollSetupPage />} />
          <Route path="/setup/classroom" element={<ClassroomSetupPage />} />
          {/* Legacy generic setup route — still functional */}
          <Route path="/setup" element={<SetupPage />} />
          {/* Gameplay routes */}
          <Route path="/play" element={<GameplayPage />} />
          <Route path="/board" element={<BoardPage />} />
          <Route path="/results" element={<ResultsPage />} />
        </Routes>
      </GameProvider>
    </ThemeProvider>
  );
}

/** Resolved once at module load — native apps use HashRouter, web uses BrowserRouter. */
const Router = isNativePlatform() ? HashRouter : BrowserRouter;

export function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}
